import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Layout from '@/components/layout/Layout';
import Container from '@/components/layout/Container';
import Home from '@/pages/Home';
import LoadingSpinner from '@/components/feedback/LoadingSpinner';
import Skeleton, { ArticleCardSkeleton } from '@/components/feedback/Skeleton';

// All pages except Home are lazy-loaded so the initial bundle ships only
// what's needed for the landing page. React Router will Suspense each route
// while its chunk downloads. Order: keep Home eager (LCP candidate); lazy
// the rest (Article is the most-visited but acceptable to lazy because
// users always come via a fresh URL → eager fetch + parse is fine).
// 2026-05-26 — Patch v8 personas : remplace l'ancien StartHere (3 cards
// "M+12/M-3/déjà arrivé") par le nouveau picker 5 personas canon +
// intent + pet_owner. L'ancienne page StartHere.jsx reste en archive
// pour ref mais n'est plus importée.
const StartHerePersonaPicker = lazy(() =>
  import('@/components/personas/StartHerePersonaPicker'),
);
const About = lazy(() => import('@/pages/About'));
const Article = lazy(() => import('@/pages/Article'));
const Flights = lazy(() => import('@/pages/Flights'));
const RemoteWork = lazy(() => import('@/pages/RemoteWork'));
const FreeTools = lazy(() => import('@/pages/FreeTools'));
const SingleToolPage = lazy(() => import('@/pages/tools/SingleToolPage'));
const Consulting = lazy(() => import('@/pages/Consulting'));
const Newsletter = lazy(() => import('@/pages/Newsletter'));
const Resources = lazy(() => import('@/pages/Resources'));
const Videos = lazy(() => import('@/pages/Videos'));
const Gallery = lazy(() => import('@/pages/Gallery'));
const Shop = lazy(() => import('@/pages/Shop'));
const ComparisonsHub = lazy(() => import('@/pages/ComparisonsHub'));
const ComparisonPage = lazy(() => import('@/pages/ComparisonPage'));
const Legal = lazy(() => import('@/pages/Legal'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Preview = lazy(() => import('@/pages/Preview'));
const LibraryRedirect = lazy(() => import('@/pages/library/LibraryRedirect'));
const ShopRedirect = lazy(() => import('@/pages/ShopRedirect'));
const Category = lazy(() => import('@/pages/Category'));
const LeadMagnet = lazy(() => import('@/pages/LeadMagnet'));
const Bookmarks = lazy(() => import('@/pages/Bookmarks'));
const MonPanama = lazy(() => import('@/pages/MonPanama'));
const Author = lazy(() => import('@/pages/Author'));
const PanamaMap = lazy(() => import('@/pages/PanamaMap'));
const PanamaHistory = lazy(() => import('@/pages/PanamaHistory'));
const Methodology = lazy(() => import('@/pages/Methodology'));
// 2026-05-26 — Sales landing dédiée Pack Entrepreneur $49 (sku pack_business_setup).
const SalesPackEntrepreneur = lazy(() => import('@/pages/SalesPackEntrepreneur'));
const ProductPage = lazy(() => import('@/pages/ProductPage'));
const Blog = lazy(() => import('@/pages/Blog'));
const PressKit = lazy(() => import('@/pages/PressKit'));
const Place = lazy(() => import('@/pages/Place'));
const Voyage = lazy(() => import('@/pages/Voyage'));
const Partenaires = lazy(() => import('@/pages/Partenaires'));
const Enfants = lazy(() => import('@/pages/Enfants'));
// 2026-06-25 — Landing pensionado / retraite. Complément du mode confort
// (toggle Header) pour l'audience retraitée — produit Pensionado Plus.
const Retirees = lazy(() => import('@/pages/Retirees'));
const MyRoadmap = lazy(() => import('@/pages/MyRoadmap'));
const Questions = lazy(() => import('@/pages/Questions'));
const MoveIn60 = lazy(() => import('@/pages/MoveIn60'));
const ProgrammeLecteurs = lazy(() => import('@/pages/ProgrammeLecteurs'));
const Sitemap = lazy(() => import('@/pages/Sitemap'));
// 2026-05-28 — Quiz "Prêt à déménager au Panama ?" (page dédiée + composant).
const QuizReady = lazy(() => import('@/pages/QuizReady'));
// 2026-05-29 — Page goodies gratuits (fonds d'écran + kit logo).
const BrandDownloads = lazy(() => import('@/pages/BrandDownloads'));
// Internal admin dashboard — sits OUTSIDE the locale tree (no lang prefix).
// Authenticated via /api/admin-auth (ADMIN_PASSWORD env var); noindex.
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const RefundsAdmin = lazy(() => import('@/pages/RefundsAdmin'));
const StatusPage = lazy(() => import('@/pages/StatusPage'));
const Roadmap = lazy(() => import('@/pages/Roadmap'));
const Merci = lazy(() => import('@/pages/Merci'));

import { SUPPORTED_LANGS, DEFAULT_LANG, LANG_STORAGE_KEY } from '@/i18n';
import { trackPageView } from '@/utils/analytics';
import { ROUTES, categoryPathSlugsForLang, pathFor } from '@/data/routes';

// Strips the optional /en or /es prefix from a path so we can build
// language-specific route trees with paths *relative* to that root.
function stripPrefix(path, lang) {
  if (lang === 'fr') return path;
  const prefix = `/${lang}`;
  if (path === prefix) return '/';
  if (path.startsWith(prefix + '/')) return path.slice(prefix.length);
  return path;
}

function relRoute(routeKey, lang) {
  return stripPrefix(ROUTES[routeKey][lang], lang);
}

// Reads the language from the path prefix and syncs i18next + <html lang>.
//
// 2026-05-17 — Simplified : the URL is the canonical source of truth for
// the active locale. /en/* → en, /es/* → es, anything else → fr. The
// previous version fell back to localStorage when no prefix was present,
// which made /histoire (FR canonical) render as EN when the user had
// EN cached. Bad for users AND for SEO (hreflang mismatch).
//
// localStorage is no longer consulted here — the language-switcher writes
// to it for analytics/preference tracking, but URL routing always wins.
function LanguageSync() {
  const { i18n } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    const segment = location.pathname.split('/').filter(Boolean)[0];
    const target = SUPPORTED_LANGS.includes(segment) ? segment : DEFAULT_LANG;

    if (target !== i18n.language) i18n.changeLanguage(target);
    document.documentElement.lang = target;
    // Keep storage in sync for the language switcher's UI state, but the
    // value is no longer used for routing.
    try {
      if (typeof window !== 'undefined') localStorage.setItem(LANG_STORAGE_KEY, target);
    } catch { /* private mode — ignore */ }
  }, [i18n, location.pathname]);

  return null;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);
  return null;
}

// Route-level Suspense boundary. Picks a content-shaped skeleton based
// on the URL pattern so the user sees the layout they're about to get
// instead of a centered spinner. Reduces perceived wait + prevents
// layout shift when the chunk arrives.
function RouteFallback() {
  if (typeof window === 'undefined') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size={48} />
      </div>
    );
  }
  const path = window.location.pathname;

  // List-style pages (Blog / Category / Bookmarks / Comparisons hub) → grid
  const isListPage =
    /\/(blog|favoris|favorites|favoritos|comparatifs|comparisons|comparativas)(\/|$)/.test(path) ||
    // Any direct category landing page (path is /catSlug or /lang/catSlug)
    /^\/(en\/|es\/)?[a-z-]+\/?$/.test(path);

  if (isListPage) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-10">
        <Skeleton width={120} height={12} className="mb-3" />
        <Skeleton width="40%" height={32} className="mb-2" />
        <Skeleton width="65%" height={18} className="mb-10" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <ArticleCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  // Article-style pages (anything with two URL segments after the locale)
  const segments = path.split('/').filter(Boolean);
  const isArticle =
    segments.length >= 2 ||
    (segments.length === 2 && ['en', 'es'].includes(segments[0]));

  if (isArticle) {
    return (
      <div className="mx-auto max-w-article px-4 py-8">
        <div className="mb-6 flex gap-2">
          <Skeleton width={60} height={12} />
          <Skeleton width={4} height={12} />
          <Skeleton width={80} height={12} />
        </div>
        <Skeleton width={120} height={12} className="mb-3" />
        <Skeleton width="85%" height={36} className="mb-3" />
        <Skeleton width="60%" height={36} className="mb-6" />
        <Skeleton width="100%" height={18} className="mb-2" />
        <Skeleton width="78%" height={18} className="mb-8" />
        <Skeleton width="100%" height={320} className="mb-10" />
        <Skeleton width="40%" height={24} className="mb-4" />
        <Skeleton width="100%" height={14} className="mb-2" />
        <Skeleton width="98%" height={14} className="mb-2" />
        <Skeleton width="92%" height={14} className="mb-2" />
        <Skeleton width="75%" height={14} />
      </div>
    );
  }

  // Generic fallback
  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Skeleton width={140} height={14} className="mb-4" />
      <Skeleton width="65%" height={32} className="mb-3" />
      <Skeleton width="100%" height={16} className="mb-2" />
      <Skeleton width="90%" height={16} className="mb-8" />
      <Skeleton width="100%" height={240} className="mb-8" />
      <Skeleton width="100%" height={14} className="mb-2" />
      <Skeleton width="95%" height={14} className="mb-2" />
      <Skeleton width="80%" height={14} />
    </div>
  );
}

// Builds the localized route tree for a language. Each language has its
// own set of slugs (Home / Start Here / About / Flights / …) plus a wildcard
// `:categorySlug/:articleSlug` route that picks up every category in the
// language. Articles fall through to <Article> which handles 404 itself.
function LocaleRoutes({ lang }) {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route
            path={relRoute('startHere', lang).slice(1)}
            element={
              <Container className="py-16">
                <StartHerePersonaPicker
                  onComplete={(slug, intent) => {
                    // T5 (value ladder) — route par STADE du parcours (axe intent) :
                    //   exploring (rêve)     → blog (contenu découverte, zéro push produit)
                    //   planning (recherche) → /ma-roadmap (la feuille de route gamifiée
                    //                          qui montre les 6-7 étapes à cocher pour
                    //                          cette persona — couleur de la persona,
                    //                          progression sauvegardée — 2026-06-26)
                    //   move-soon (décidé)   → /shop (paliers Pro/Premium + consulting)
                    // Persona « etudiant » ou intent absent → entrée douce (blog).
                    const dest =
                      slug === 'etudiant'
                        ? 'home'
                        : intent === 'move-soon'
                          ? 'shop'
                          : intent === 'planning'
                            ? 'myRoadmap'
                            : intent === 'exploring'
                              ? 'blog'
                              : 'myRoadmap';
                    window.location.href = pathFor(dest, lang);
                  }}
                />
              </Container>
            }
          />
          <Route path={relRoute('about', lang).slice(1)} element={<About />} />
          <Route path={relRoute('flights', lang).slice(1)} element={<Flights />} />
          <Route path={relRoute('remoteWork', lang).slice(1)} element={<RemoteWork />} />
          <Route path={relRoute('tools', lang).slice(1)} element={<FreeTools />} />
          {/* 2026-05-26 — Audit Sprint 3 (C·03+) : 4 outils = 4 URLs dédiées. */}
          <Route path={relRoute('toolBudget', lang).slice(1)} element={<SingleToolPage tool="budget" />} />
          <Route path={relRoute('toolConverter', lang).slice(1)} element={<SingleToolPage tool="converter" />} />
          <Route path={relRoute('toolVisa', lang).slice(1)} element={<SingleToolPage tool="visa" />} />
          <Route path={relRoute('toolDog', lang).slice(1)} element={<SingleToolPage tool="dog" />} />
          <Route path={relRoute('consulting', lang).slice(1)} element={<Consulting />} />
          <Route path={relRoute('newsletter', lang).slice(1)} element={<Newsletter />} />
          <Route path={relRoute('resources', lang).slice(1)} element={<Resources />} />
          <Route path={relRoute('videos', lang).slice(1)} element={<Videos />} />
          <Route path={relRoute('gallery', lang).slice(1)} element={<Gallery />} />
          <Route path={relRoute('shop', lang).slice(1)} element={<Shop />} />
          {/* /shop/:slug — used by article CTAs. Redirects to the
              configured Gumroad checkout URL via paymentLinks.js, or
              falls back to the catalog if the slug is unknown.
              Without this, the 20+ article CTAs would 404. */}
          <Route path={`${relRoute('shop', lang).slice(1)}/:slug`} element={<ShopRedirect />} />
          <Route path={relRoute('blog', lang).slice(1)} element={<Blog />} />
          <Route path={relRoute('pressKit', lang).slice(1)} element={<PressKit />} />
          <Route path={relRoute('voyage', lang).slice(1)} element={<Voyage />} />
          <Route path={relRoute('partenaires', lang).slice(1)} element={<Partenaires />} />
          <Route path={relRoute('enfants', lang).slice(1)} element={<Enfants />} />
          <Route path={relRoute('retirees', lang).slice(1)} element={<Retirees />} />
          <Route path={relRoute('myRoadmap', lang).slice(1)} element={<MyRoadmap />} />
          <Route path={relRoute('questions', lang).slice(1)} element={<Questions />} />
          {/* Landing lead magnet « Move in 60 Days » — cours email gratuit (G1). */}
          <Route path={relRoute('move60', lang).slice(1)} element={<MoveIn60 />} />
          {/* Page réservée (noindex) du programme lecteurs-testeurs pré-lancement. */}
          <Route path={relRoute('programmeLecteurs', lang).slice(1)} element={<ProgrammeLecteurs />} />
          {/* Sales landing page Pack Entrepreneur $49 — punchy URL marketing. */}
          <Route path={relRoute('packEntrepreneur', lang).slice(1)} element={<SalesPackEntrepreneur />} />
          {/* Pages produit dédiées /produit/:slug — registre src/data/products.js,
              gated par le flag `visible` (caché = noindex + hors grille Shop). */}
          <Route path={`${relRoute('product', lang).slice(1)}/:slug`} element={<ProductPage />} />
          {/* 2026-05-26 — Sprint 4 : page /plan-du-site qu'on a référencée
              dans le Footer slim (Sprint 2). Liste plate de toutes les pages. */}
          <Route path={relRoute('sitemap', lang).slice(1)} element={<Sitemap />} />
          {/* Quiz "Prêt à déménager ?" — page dédiée indexable. */}
          <Route path={relRoute('quizReady', lang).slice(1)} element={<QuizReady />} />
          {/* Goodies gratuits — fonds d'écran + kit logo téléchargeables. */}
          <Route path={relRoute('downloads', lang).slice(1)} element={<BrandDownloads />} />
          <Route path={relRoute('comparisons', lang).slice(1)} element={<ComparisonsHub />} />
          <Route path={`${relRoute('comparisons', lang).slice(1)}/:slug`} element={<ComparisonPage />} />
          <Route path={relRoute('bookmarks', lang).slice(1)} element={<Bookmarks />} />
          <Route path={relRoute('monPanama', lang).slice(1)} element={<MonPanama />} />
          <Route path={relRoute('map', lang).slice(1)} element={<PanamaMap />} />
          <Route path={relRoute('history', lang).slice(1)} element={<PanamaHistory />} />
          <Route path={relRoute('methodology', lang).slice(1)} element={<Methodology />} />
          <Route path={relRoute('preview', lang).slice(1)} element={<Preview />} />

          {/* Library — post-purchase HMAC-gated content (LS webhook delivers token via Beehiiv email) */}
          <Route path="library/:slug" element={<LibraryRedirect />} />

          {/* Place pages — landmarks, parks, neighbourhoods (lieux/places/lugares) */}
          <Route path={`${relRoute('places', lang).slice(1)}/:slug`} element={<Place />} />

          {/* Author profile pages — lang-specific URL segment */}
          <Route
            path={`${lang === 'en' ? 'authors' : lang === 'es' ? 'autores' : 'auteurs'}/:slug`}
            element={<Author />}
          />

          <Route path="legal/:slug" element={<Legal />} />
          <Route path="lead/:slug" element={<LeadMagnet />} />

          {/* Article pages — registered per category pathSlug for this lang.
              Order matters: article (with :slug) must come BEFORE the bare
              category route, otherwise the category route would steal
              article URLs that have a 2nd segment. */}
          {categoryPathSlugsForLang(lang).map((cat) => (
            <Route key={`${cat}-article`} path={`${cat}/:slug`} element={<Article />} />
          ))}
          {/* Category landing pages — list articles in the category. */}
          {categoryPathSlugsForLang(lang).map((cat) => (
            <Route key={`${cat}-category`} path={cat} element={<Category />} />
          ))}

          {/* Catch-all for unknown URLs in this locale */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <>
      <LanguageSync />
      <PageViewTracker />
      <Routes>
        {/* Admin dashboard — outside the locale tree, no Layout wrapper.
            2026-05-18: moved from /admin (publicly known) to a secret
            slug. /admin is left as a 301 to the homepage so bots and
            curious visitors can't tell anything exists at /admin.
            Update via .env / Vite env var if you ever need to rotate.
            Current slug: /qg-jade-2026 (memorable, French, low-collision). */}
        <Route
          path="/qg-jade-2026"
          element={
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size={48} /></div>}>
              <AdminDashboard />
            </Suspense>
          }
        />
        {/* Refunds admin — sous le même slug secret, auth via X-Admin-Token. */}
        <Route
          path="/qg-jade-2026/refunds"
          element={
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size={48} /></div>}>
              <RefundsAdmin />
            </Suspense>
          }
        />
        {/* Public status page — uptime + smoke test data */}
        <Route
          path="/status"
          element={
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size={48} /></div>}>
              <StatusPage />
            </Suspense>
          }
        />
        {/* Roadmap publique — lit Notion DB Products */}
        <Route
          path="/roadmap"
          element={
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size={48} /></div>}>
              <Roadmap />
            </Suspense>
          }
        />
        {/* Page merci post-NPS */}
        <Route
          path="/merci"
          element={
            <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size={48} /></div>}>
              <Merci />
            </Suspense>
          }
        />
        {/* Sink the old public URL */}
        <Route path="/admin" element={<Navigate to="/" replace />} />
        <Route path="/en/*" element={<LocaleRoutes lang="en" />} />
        <Route path="/es/*" element={<LocaleRoutes lang="es" />} />
        <Route path="/*"    element={<LocaleRoutes lang="fr" />} />
        <Route path="*"     element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
